import { create } from 'zustand';
import { toast } from 'sonner';
import type {
  Plan,
  Week,
  Session,
  ExerciseInstance,
  ExerciseDefinition,
  AppTab,
} from '../types';
import {
  createEmptyPlan,
  createWeek,
  createSession,
  createExerciseInstance,
  reorder,
  uid,
} from '../utils/helpers';
import { plansAPI } from '../lib/api';

// ── Store interface ─────────────────────────

interface PlanState {
  // Multi-plan library
  savedPlans: Plan[];
  activePlanId: string | null;

  // Current editing state
  plan: Plan | null;
  activeWeekIndex: number;
  activeTab: AppTab;

  // API state
  loading: boolean;
  error: string | null;
  saving: boolean;
  lastSaved: string | null;

  // Initialize - fetch plans from API
  fetchPlans: () => Promise<void>;

  // Plan CRUD
  createPlan: (overrides?: Partial<Plan>) => Promise<void>;
  loadPlan: (plan: Plan) => void;
  clearPlan: () => void;
  updatePlanConfig: (updates: Partial<Plan>) => void;

  // Multi-plan management
  openPlan: (planId: string) => void;
  savePlanToLibrary: () => Promise<void>;
  deletePlan: (planId: string) => Promise<void>;
  duplicatePlan: (planId: string) => Promise<void>;
  goToDashboard: () => void;

  // Tab / navigation
  setActiveTab: (tab: AppTab) => void;
  setActiveWeek: (index: number) => void;

  // Week ops
  addWeek: () => void;
  removeWeek: (weekId: string) => void;
  duplicateWeek: (weekId: string) => void;
  toggleDeload: (weekId: string) => void;
  syncWeeksToMesocycleLength: () => void;

  // Session ops
  addSession: (weekId: string) => void;
  updateSession: (weekId: string, sessionId: string, updates: Partial<Session>) => void;
  removeSession: (weekId: string, sessionId: string) => void;
  duplicateSession: (weekId: string, sessionId: string) => void;
  reorderSessions: (weekId: string, from: number, to: number) => void;

  // Exercise ops
  addExercise: (weekId: string, sessionId: string, def: ExerciseDefinition) => void;
  updateExercise: (weekId: string, sessionId: string, exerciseId: string, updates: Partial<ExerciseInstance>) => void;
  removeExercise: (weekId: string, sessionId: string, exerciseId: string) => void;
  reorderExercises: (weekId: string, sessionId: string, from: number, to: number) => void;

  // Warmup exercise ops
  addWarmupExercise: (weekId: string, sessionId: string, def: ExerciseDefinition) => void;
  updateWarmupExercise: (weekId: string, sessionId: string, exerciseId: string, updates: Partial<ExerciseInstance>) => void;
  removeWarmupExercise: (weekId: string, sessionId: string, exerciseId: string) => void;
  reorderWarmupExercises: (weekId: string, sessionId: string, from: number, to: number) => void;
}

// ── Helpers ──────────────────────────────────

function touchPlan(plan: Plan): Plan {
  return { ...plan, updatedAt: new Date().toISOString() };
}

function mapWeek(plan: Plan, weekId: string, fn: (w: Week) => Week): Plan {
  return touchPlan({
    ...plan,
    weeks: plan.weeks.map((w) => (w.id === weekId ? fn(w) : w)),
  });
}

function mapSession(week: Week, sessionId: string, fn: (s: Session) => Session): Week {
  return { ...week, sessions: week.sessions.map((s) => (s.id === sessionId ? fn(s) : s)) };
}

/** Convert MongoDB plan to frontend Plan format */
function mapPlanFromAPI(apiPlan: any): Plan {
  return {
    id: apiPlan._id,
    name: apiPlan.name,
    goal: apiPlan.goal,
    trainingLevel: apiPlan.trainingLevel,
    mesocycleLength: apiPlan.mesocycleLength,
    frequency: apiPlan.frequency,
    fatigueTolerance: apiPlan.fatigueTolerance,
    progressionModel: apiPlan.progressionModel,
    weeks: apiPlan.weeks || [],
    createdAt: apiPlan.createdAt,
    updatedAt: apiPlan.updatedAt,
  };
}

/** Convert frontend Plan to API format */
function mapPlanToAPI(plan: Plan) {
  const { id, ...rest } = plan;
  return rest;
}

// Debounce helper
let saveTimeout: NodeJS.Timeout | null = null;

// ── Store ───────────────────────────────────

export const usePlanStore = create<PlanState>((set, get) => ({
  savedPlans: [],
  activePlanId: null,
  plan: null,
  activeWeekIndex: 0,
  activeTab: 'dashboard' as AppTab,
  loading: false,
  error: null,
  saving: false,
  lastSaved: null,

  // ── API Operations ────────────────────────
  
  fetchPlans: async () => {
    set({ loading: true, error: null });
    try {
      const { plans } = await plansAPI.list();
      const mapped = plans.map(mapPlanFromAPI);
      set({ savedPlans: mapped, loading: false });
    } catch (error: any) {
      const message = error.message || 'Nie udało się wczytać planów';
      set({ error: message, loading: false });
      toast.error(message);
      console.error('Failed to fetch plans:', error);
    }
  },

  // ── Plan CRUD ────────────────────────
  
  createPlan: async (overrides) => {
    const plan = createEmptyPlan(overrides);
    while (plan.weeks.length < plan.mesocycleLength) {
      plan.weeks.push(createWeek(plan.weeks.length + 1));
    }
    const days = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'];
    for (const week of plan.weeks) {
      if (week.sessions.length === 0) {
        for (let i = 0; i < plan.frequency && i < 7; i++) {
          week.sessions.push(createSession(days[i], 'Ogólne'));
        }
      }
    }

    set({ saving: true, error: null });
    try {
      const apiData = mapPlanToAPI(plan);
      const { plan: savedPlan } = await plansAPI.create(apiData);
      const mapped = mapPlanFromAPI(savedPlan);
      
      set((s) => ({
        plan: mapped,
        activePlanId: mapped.id,
        savedPlans: [...s.savedPlans, mapped],
        activeWeekIndex: 0,
        activeTab: 'builder',
        saving: false,
        lastSaved: new Date().toISOString(),
      }));
      toast.success('Plan utworzony!');
    } catch (error: any) {
      const message = error.message || 'Nie udało się utworzyć planu';
      set({ error: message, saving: false });
      toast.error(message);
      console.error('Failed to create plan:', error);
      throw error;
    }
  },

  loadPlan: (plan) => {
    set({
      plan,
      activePlanId: plan.id,
      activeWeekIndex: 0,
      activeTab: 'builder',
    });
  },

  clearPlan: () => {
    set({
      plan: null,
      activePlanId: null,
      activeWeekIndex: 0,
      activeTab: 'dashboard',
    });
  },

  updatePlanConfig: (updates) => {
    const { plan } = get();
    if (!plan) return;
    const updated = touchPlan({ ...plan, ...updates });
    set({ plan: updated });
    
    if (updates.mesocycleLength) {
      get().syncWeeksToMesocycleLength();
    }
    
    // Trigger auto-save
    debouncedSave(get);
  },

  // ── Multi-plan management ────────────
  
  openPlan: (planId) => {
    const { savedPlans } = get();
    const target = savedPlans.find((p) => p.id === planId);
    if (!target) return;
    
    set({
      plan: target,
      activePlanId: planId,
      activeWeekIndex: 0,
      activeTab: 'builder',
    });
  },

  savePlanToLibrary: async () => {
    const { plan } = get();
    if (!plan) return;

    set({ saving: true, error: null });
    try {
      const apiData = mapPlanToAPI(plan);
      const { plan: savedPlan } = await plansAPI.update(plan.id, apiData);
      const mapped = mapPlanFromAPI(savedPlan);
      
      set((s) => ({
        plan: mapped,
        savedPlans: s.savedPlans.map((p) => (p.id === mapped.id ? mapped : p)),
        saving: false,
        lastSaved: new Date().toISOString(),
      }));
      toast.success('Plan zapisany!');
    } catch (error: any) {
      const message = error.message || 'Nie udało się zapisać planu';
      set({ error: message, saving: false });
      toast.error(message);
      console.error('Failed to save plan:', error);
      throw error;
    }
  },

  deletePlan: async (planId) => {
    set({ loading: true, error: null });
    try {
      await plansAPI.delete(planId);
      
      const { activePlanId } = get();
      set((s) => {
        const filtered = s.savedPlans.filter((p) => p.id !== planId);
        
        if (activePlanId === planId) {
          return {
            savedPlans: filtered,
            plan: null,
            activePlanId: null,
            activeTab: 'dashboard',
            loading: false,
          };
        }
        
        return { savedPlans: filtered, loading: false };
      });
      toast.success('Plan usunięty');
    } catch (error: any) {
      const message = error.message || 'Nie udało się usunąć planu';
      set({ error: message, loading: false });
      toast.error(message);
      console.error('Failed to delete plan:', error);
      throw error;
    }
  },

  duplicatePlan: async (planId) => {
    const { savedPlans } = get();
    const source = savedPlans.find((p) => p.id === planId);
    if (!source) return;
    
    const clone: Plan = {
      ...JSON.parse(JSON.stringify(source)),
      id: uid(),
      name: `${source.name} (kopia)`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    
    clone.weeks = clone.weeks.map((w: Week) => ({
      ...w,
      id: uid(),
      sessions: w.sessions.map((s: Session) => ({
        ...s,
        id: uid(),
        warmupExercises: (s.warmupExercises || []).map((e: ExerciseInstance) => ({ ...e, id: uid() })),
        exercises: s.exercises.map((e: ExerciseInstance) => ({ ...e, id: uid() })),
      })),
    }));

    set({ saving: true, error: null });
    try {
      const apiData = mapPlanToAPI(clone);
      const { plan: savedPlan } = await plansAPI.create(apiData);
      const mapped = mapPlanFromAPI(savedPlan);
      
      set((s) => ({
        savedPlans: [...s.savedPlans, mapped],
        saving: false,
        lastSaved: new Date().toISOString(),
      }));
      toast.success('Plan zduplikowany!');
    } catch (error: any) {
      const message = error.message || 'Nie udało się zduplikować planu';
      set({ error: message, saving: false });
      toast.error(message);
      console.error('Failed to duplicate plan:', error);
      throw error;
    }
  },

  goToDashboard: () => {
    set({
      plan: null,
      activePlanId: null,
      activeWeekIndex: 0,
      activeTab: 'dashboard',
    });
  },

  // ── Tab ──────────────────────────────
  setActiveTab: (tab) => set({ activeTab: tab }),
  setActiveWeek: (index) => set({ activeWeekIndex: index }),

  // All mutation methods below trigger auto-save
  // (implementations remain same but call debouncedSave at end)

  // ── Week ─────────────────────────────
  addWeek: () => {
    const { plan } = get();
    if (!plan) return;
    const newWeek = createWeek(plan.weeks.length + 1);
    const lastWeek = plan.weeks[plan.weeks.length - 1];
    if (lastWeek) {
      newWeek.sessions = lastWeek.sessions.map((s) => ({
        ...createSession(s.day, s.focus),
        warmupExercises: (s.warmupExercises || []).map((e) => ({ ...e, id: uid() })),
        exercises: s.exercises.map((e) => ({ ...e, id: uid() })),
      }));
    }
    const updated = touchPlan({
      ...plan,
      weeks: [...plan.weeks, newWeek],
      mesocycleLength: plan.weeks.length + 1,
    });
    set({ plan: updated });
    debouncedSave(get);
  },

  removeWeek: (weekId) => {
    const { plan, activeWeekIndex } = get();
    if (!plan || plan.weeks.length <= 1) return;
    const filtered = plan.weeks.filter((w) => w.id !== weekId);
    filtered.forEach((w, i) => (w.weekNumber = i + 1));
    const newIndex = Math.min(activeWeekIndex, filtered.length - 1);
    const updated = touchPlan({ ...plan, weeks: filtered, mesocycleLength: filtered.length });
    set({ plan: updated, activeWeekIndex: newIndex });
    debouncedSave(get);
  },

  duplicateWeek: (weekId) => {
    const { plan } = get();
    if (!plan) return;
    const source = plan.weeks.find((w) => w.id === weekId);
    if (!source) return;
    const clone: Week = {
      id: uid(),
      weekNumber: plan.weeks.length + 1,
      deload: false,
      sessions: source.sessions.map((s) => ({
        ...s,
        id: uid(),
        warmupExercises: (s.warmupExercises || []).map((e) => ({ ...e, id: uid() })),
        exercises: s.exercises.map((e) => ({ ...e, id: uid() })),
      })),
    };
    const updated = touchPlan({
      ...plan,
      weeks: [...plan.weeks, clone],
      mesocycleLength: plan.weeks.length + 1,
    });
    set({ plan: updated });
    debouncedSave(get);
  },

  toggleDeload: (weekId) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) => ({ ...w, deload: !w.deload }));
    set({ plan: updated });
    debouncedSave(get);
  },

  syncWeeksToMesocycleLength: () => {
    const { plan } = get();
    if (!plan) return;
    let weeks = [...plan.weeks];
    while (weeks.length < plan.mesocycleLength) {
      const lastWeek = weeks[weeks.length - 1];
      const w = createWeek(weeks.length + 1);
      if (lastWeek) {
        w.sessions = lastWeek.sessions.map((s) => ({
          ...createSession(s.day, s.focus),
          warmupExercises: (s.warmupExercises || []).map((e) => ({ ...e, id: uid() })),
          exercises: s.exercises.map((e) => ({ ...e, id: uid() })),
        }));
      }
      weeks.push(w);
    }
    while (weeks.length > plan.mesocycleLength) weeks.pop();
    weeks.forEach((w, i) => (w.weekNumber = i + 1));
    const updated = touchPlan({ ...plan, weeks });
    set({
      plan: updated,
      activeWeekIndex: Math.min(get().activeWeekIndex, weeks.length - 1),
    });
    debouncedSave(get);
  },

  // ── Session ──────────────────────────
  addSession: (weekId) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) => ({
      ...w,
      sessions: [...w.sessions, createSession('Poniedziałek', 'Ogólne')],
    }));
    set({ plan: updated });
    debouncedSave(get);
  },

  updateSession: (weekId, sessionId, updates) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) =>
      mapSession(w, sessionId, (s) => ({ ...s, ...updates }))
    );
    set({ plan: updated });
    debouncedSave(get);
  },

  removeSession: (weekId, sessionId) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) => ({
      ...w,
      sessions: w.sessions.filter((s) => s.id !== sessionId),
    }));
    set({ plan: updated });
    debouncedSave(get);
  },

  duplicateSession: (weekId, sessionId) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) => {
      const source = w.sessions.find((s) => s.id === sessionId);
      if (!source) return w;
      const clone: Session = {
        ...source,
        id: uid(),
        warmupExercises: (source.warmupExercises || []).map((e) => ({ ...e, id: uid() })),
        exercises: source.exercises.map((e) => ({ ...e, id: uid() })),
      };
      return { ...w, sessions: [...w.sessions, clone] };
    });
    set({ plan: updated });
    debouncedSave(get);
  },

  reorderSessions: (weekId, from, to) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) => ({
      ...w,
      sessions: reorder(w.sessions, from, to),
    }));
    set({ plan: updated });
    debouncedSave(get);
  },

  // ── Exercise ─────────────────────────
  addExercise: (weekId, sessionId, def) => {
    const { plan } = get();
    if (!plan) return;
    const instance = createExerciseInstance(def);
    const updated = mapWeek(plan, weekId, (w) =>
      mapSession(w, sessionId, (s) => ({
        ...s,
        exercises: [...s.exercises, instance],
      }))
    );
    set({ plan: updated });
    debouncedSave(get);
  },

  updateExercise: (weekId, sessionId, exerciseId, updates) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) =>
      mapSession(w, sessionId, (s) => ({
        ...s,
        exercises: s.exercises.map((e) =>
          e.id === exerciseId ? { ...e, ...updates } : e
        ),
      }))
    );
    set({ plan: updated });
    debouncedSave(get);
  },

  removeExercise: (weekId, sessionId, exerciseId) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) =>
      mapSession(w, sessionId, (s) => ({
        ...s,
        exercises: s.exercises.filter((e) => e.id !== exerciseId),
      }))
    );
    set({ plan: updated });
    debouncedSave(get);
  },

  reorderExercises: (weekId, sessionId, from, to) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) =>
      mapSession(w, sessionId, (s) => ({
        ...s,
        exercises: reorder(s.exercises, from, to),
      }))
    );
    set({ plan: updated });
    debouncedSave(get);
  },

  // ── Warmup Exercise ──────────────────
  addWarmupExercise: (weekId, sessionId, def) => {
    const { plan } = get();
    if (!plan) return;
    const instance = createExerciseInstance(def);
    const updated = mapWeek(plan, weekId, (w) =>
      mapSession(w, sessionId, (s) => ({
        ...s,
        warmupExercises: [...(s.warmupExercises || []), instance],
      }))
    );
    set({ plan: updated });
    debouncedSave(get);
  },

  updateWarmupExercise: (weekId, sessionId, exerciseId, updates) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) =>
      mapSession(w, sessionId, (s) => ({
        ...s,
        warmupExercises: (s.warmupExercises || []).map((e) =>
          e.id === exerciseId ? { ...e, ...updates } : e
        ),
      }))
    );
    set({ plan: updated });
    debouncedSave(get);
  },

  removeWarmupExercise: (weekId, sessionId, exerciseId) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) =>
      mapSession(w, sessionId, (s) => ({
        ...s,
        warmupExercises: (s.warmupExercises || []).filter((e) => e.id !== exerciseId),
      }))
    );
    set({ plan: updated });
    debouncedSave(get);
  },

  reorderWarmupExercises: (weekId, sessionId, from, to) => {
    const { plan } = get();
    if (!plan) return;
    const updated = mapWeek(plan, weekId, (w) =>
      mapSession(w, sessionId, (s) => ({
        ...s,
        warmupExercises: reorder(s.warmupExercises || [], from, to),
      }))
    );
    set({ plan: updated });
    debouncedSave(get);
  },
}));

// ── Auto-save with debounce ──────────────────

function debouncedSave(get: () => PlanState) {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }
  
  saveTimeout = setTimeout(async () => {
    const { plan, savePlanToLibrary } = get();
    if (plan) {
      try {
        await savePlanToLibrary();
      } catch (error) {
        console.error('Auto-save failed:', error);
      }
    }
  }, 1000); // 1 second debounce
}
